/** Tool catalogue and tool pickers (ADR-0060). */
export default {
  en: {
    title: "Tool catalogue",
    intro: "Every kind of system agents can work with, and what each one can do here. Connected tools are ready for agents; the others say what is missing.",
    environment: "Status in {{env}}",
    summary: "{{connected}} connected · {{registered}} waiting for release · {{available}} need setup",
    showRetired: "Show retired tools",
    filter: { label: "Show", all: "All", connected: "Connected", registered: "Waiting for release", suspended: "Paused", available: "Needs setup", retired: "Retired" },
    empty: "Nothing matches this filter.",
    status: { connected: "Connected", registered: "Waiting for release", suspended: "Paused", retired: "Retired", available: "Needs setup" },
    statusHelp: {
      connected: "Registered and released to this stage: agents can use it.",
      registered: "Added, but not released here yet. A tool administrator releases it from Governance › Releases.",
      suspended: "Switched off by an administrator. Agents cannot use it until it is reactivated.",
      retired: "Taken out of the catalogue. Agents cannot use it.",
      available: "The platform can connect to this kind of system, but none is set up here.",
    },
    category: { databases: "Database", "business-apps": "Business system", messaging: "Messaging", files: "Files and documents", identity: "People and groups", compute: "Calculations", web: "Web", other: "Other" },
    kindOf: "Kind: {{kind}}",
    source: {
      mcp: "MCP server",
      api: "API",
      database: "Database",
      builtin: "Built-in tool",
      connector: "Connector",
    },
    sourceHelp: {
      mcp: "Connected from an MCP server. It becomes a system your agents can use, with the same checks as any other: allowed actions, rules, approvals and audit.",
      api: "Connected from a web API (REST or SOAP).",
      database: "Connected to a database through named, safe queries.",
      builtin: "Part of the platform. It runs on our own servers.",
      connector: "A connector the platform ships (e-mail, files, messages and more).",
    },
    canDo: "What it can do",
    readsOnly: "Reads only",
    changesData: "Changes data",
    needsApproval: "Needs approval",
    approvers: "Approved by: {{roles}}",
    undo: "Can be undone ({{action}})",
    needsTitle: "What setting it up needs",
    notLocal: "Not in the local stack",
    setupDocs: "Set-up steps in the platform documentation: ",
    setupHelp: "Your platform team has the step-by-step set-up guide.",
    tech: { name: "Technical name", version: "Version", owner: "Maintained by (team)", setup: "Set-up guide" },
    connectGuide: "Use “Connect a system” to set it up.",
    version: "Version {{version}}",
    owner: "Owner: {{owner}}",
    note: "Note: {{note}}",
    mailInbox: "Open the test inbox",
    moreActions: "{{n}} more",
    admin: { change: "Change status of {{name}}", suspend: "Suspend", reactivate: "Reactivate", retire: "Retire", deprecate: "Deprecate", block: "Block", revoke: "Revoke" },
    sites: {
      title: "Allowed websites",
      help: "Agents using the browser can open only these sites. Every page is checked before it opens, and other sites are refused.",
      empty: "No websites allowed: the browser cannot open any page.",
      changeAt: "Change in Administration › Internet access",
      notInProxy: "Not through the proxy yet",
      notInProxyHelp: "Allowed here, but the network proxy does not let it through yet. Ask the platform team to add it to the proxy.",
      proxyList: "The network proxy lets through: {{list}}",
      proxyNone: "The network proxy lets no website through.",
      edit: "Edit allowed websites",
      editTitle: "Allowed websites for {{name}}",
      editHelp: "Type a website address such as example.com and press Enter. Use *.example.com for all of its sub-domains. Removing a site stops agents opening it from their next page.",
      field: "Websites",
      invalid: "“{{value}}” is not a website address. Use a name such as example.com, without https:// or a path.",
      willBeOutside: "The network proxy does not let these through yet, so agents will still be refused: {{list}}",
      save: "Save",
      saved: "Saved. The change applies to the next page an agent opens.",
    },
    picker: {
      group: { connected: "Ready to use", registered: "Waiting for release", suspended: "Paused" },
      reason: {
        registered: "Not released to this stage yet — a tool administrator must deploy it first.",
        suspended: "Switched off by an administrator.",
      },
      changesData: "changes data",
    },
  },
  ar: {
    title: "كتالوج الأدوات",
    intro: "كل أنواع الأنظمة التي يستطيع الوكلاء العمل معها، وما يمكن لكل منها فعله هنا. الأدوات المتصلة جاهزة للوكلاء، والبقية توضّح ما ينقصها.",
    environment: "الحالة في {{env}}",
    summary: "{{connected}} متصلة · {{registered}} بانتظار الإصدار · {{available}} تحتاج إلى إعداد",
    showRetired: "إظهار الأدوات المُتقاعدة",
    filter: { label: "عرض", all: "الكل", connected: "متصلة", registered: "بانتظار الإصدار", suspended: "موقوفة مؤقتاً", available: "تحتاج إلى إعداد", retired: "مُتقاعدة" },
    empty: "لا يوجد ما يطابق هذا الاختيار.",
    status: { connected: "متصلة", registered: "بانتظار الإصدار", suspended: "موقوفة مؤقتاً", retired: "مُتقاعدة", available: "تحتاج إلى إعداد" },
    statusHelp: {
      connected: "مسجلة ومُصدَرة إلى هذه المرحلة: يمكن للوكلاء استخدامها.",
      registered: "أُضيفت لكنها لم تُصدَر هنا بعد. يُصدرها مسؤول الأدوات من الحوكمة › الإصدارات.",
      suspended: "أوقفها مسؤول. لا يمكن للوكلاء استخدامها حتى يُعاد تفعيلها.",
      retired: "أُخرجت من الكتالوج. لا يمكن للوكلاء استخدامها.",
      available: "تستطيع المنصة الاتصال بهذا النوع من الأنظمة، لكن لم يُعدّ أي منها هنا.",
    },
    category: { databases: "قاعدة بيانات", "business-apps": "نظام أعمال", messaging: "المراسلة", files: "الملفات والمستندات", identity: "الأشخاص والمجموعات", compute: "العمليات الحسابية", web: "الويب", other: "أخرى" },
    kindOf: "النوع: {{kind}}",
    source: {
      mcp: "خادم MCP",
      api: "واجهة برمجية",
      database: "قاعدة بيانات",
      builtin: "أداة مدمجة",
      connector: "موصّل",
    },
    sourceHelp: {
      mcp: "متصل من خادم MCP. يصبح نظامًا يمكن لوكلائك استخدامه، بالضوابط نفسها مثل أي نظام آخر: الإجراءات المسموح بها والقواعد والموافقات والتدقيق.",
      api: "متصل من واجهة برمجية على الويب (REST أو SOAP).",
      database: "متصل بقاعدة بيانات عبر استعلامات مسماة وآمنة.",
      builtin: "جزء من المنصة ويعمل على خوادمنا.",
      connector: "موصّل تقدّمه المنصة (البريد الإلكتروني والملفات والرسائل وغيرها).",
    },
    canDo: "ما يمكنها فعله",
    readsOnly: "قراءة فقط",
    changesData: "تغيّر البيانات",
    needsApproval: "تتطلب موافقة",
    approvers: "يعتمدها: {{roles}}",
    undo: "يمكن التراجع عنها ({{action}})",
    needsTitle: "ما يحتاجه إعدادها",
    notLocal: "غير متوفرة في البيئة المحلية",
    setupDocs: "خطوات الإعداد في وثائق المنصة: ",
    setupHelp: "لدى فريق المنصة دليل الإعداد خطوة بخطوة.",
    tech: { name: "الاسم التقني", version: "الإصدار", owner: "الفريق المسؤول", setup: "دليل الإعداد" },
    connectGuide: "استخدم «ربط نظام» لإعدادها.",
    version: "الإصدار {{version}}",
    owner: "المالك: {{owner}}",
    note: "ملاحظة: {{note}}",
    mailInbox: "فتح صندوق البريد التجريبي",
    moreActions: "{{n}} أخرى",
    admin: { change: "تغيير حالة {{name}}", suspend: "إيقاف مؤقت", reactivate: "إعادة التفعيل", retire: "إحالة إلى التقاعد", deprecate: "إيقاف الدعم", block: "حظر", revoke: "إلغاء" },
    sites: {
      title: "المواقع المسموح بها",
      help: "لا يستطيع الوكلاء الذين يستخدمون المتصفح فتح غير هذه المواقع. تُفحص كل صفحة قبل فتحها، ويُرفض أي موقع آخر.",
      empty: "لا توجد مواقع مسموح بها: لا يستطيع المتصفح فتح أي صفحة.",
      changeAt: "التغيير من الإدارة › الوصول إلى الإنترنت",
      notInProxy: "لا يمرّ عبر الوسيط بعد",
      notInProxyHelp: "مسموح به هنا، لكن وسيط الشبكة لا يسمح بمروره بعد. اطلب من فريق المنصة إضافته إلى الوسيط.",
      proxyList: "يسمح وسيط الشبكة بمرور: {{list}}",
      proxyNone: "لا يسمح وسيط الشبكة بمرور أي موقع.",
      edit: "تعديل المواقع المسموح بها",
      editTitle: "المواقع المسموح بها لـ {{name}}",
      editHelp: "اكتب عنوان موقع مثل example.com ثم اضغط Enter. استخدم ‎*.example.com‎ لكل نطاقاته الفرعية. إزالة موقع تمنع الوكلاء من فتحه بدءاً من الصفحة التالية.",
      field: "المواقع",
      invalid: "«{{value}}» ليس عنوان موقع. استخدم اسماً مثل example.com دون https:// أو مسار.",
      willBeOutside: "وسيط الشبكة لا يسمح بمرور هذه المواقع بعد، لذا سيظل الوكلاء ممنوعين من فتحها: {{list}}",
      save: "حفظ",
      saved: "تم الحفظ. يسري التغيير على الصفحة التالية التي يفتحها أي وكيل.",
    },
    picker: {
      group: { connected: "جاهزة للاستخدام", registered: "بانتظار الإصدار", suspended: "موقوفة مؤقتاً" },
      reason: {
        registered: "لم تُصدَر إلى هذه المرحلة بعد — يجب أن ينشرها مسؤول الأدوات أولاً.",
        suspended: "أوقفها مسؤول.",
      },
      changesData: "تغيّر البيانات",
    },
  },
};
